/**
 * ONE DISPLAY ORDER, ONE LABEL TABLE, THREE SURFACES (Ashok rulings,
 * 2026-09-03 - standard labels / grouped controls slice).
 *
 * Every surface that shows a buyer the business questions - the Questions
 * step, the Answers step, and the procurement detail page - renders them
 * through orderQuestionsForDisplay, so the number a buyer sees for a
 * question is the SAME on all three. Two sorts would reproduce the "3. on
 * Questions, 4. on Answers" defect this module exists to remove; a third
 * copy on the detail page would move it to "3. in the wizard, 7. on the
 * detail page". Do not write a second order. Do not number by stored index
 * anywhere a buyer can see.
 *
 * THE ORDER IS A LIFT, NOT A NEW SORT. Its base is
 * getMandatoryFieldKeysForType (lib/answersValidation.js) - the module that
 * decides what is mandatory - which already places currency beside budget
 * and start date beside end date. Mandatory questions with a canonical
 * field_key come first, in that order; mandatory-by-flag questions with no
 * canonical key follow, in stored order; optional questions follow those,
 * in stored order. Numbering is 1..N over the result. StepQuestions.jsx's
 * MANDATORY_FIELD_KEYS is NOT an order and is not used here - it is the
 * divergent fifth mandatory source recorded as CTRL-086's Known
 * Limitation.
 *
 * DISPLAY-ONLY. The business_questions array is never reordered and the
 * q{i} answer keys are untouched: every item carries its STORED `index`,
 * and that is what answers are read and written by. `number` is for eyes.
 */
import { normalizeQuestion } from '@/lib/businessQuestions';
import { getMandatoryFieldKeysForType } from '@/lib/answersValidation';

// STANDARD LABELS - a display override keyed on field_key, not a prompt
// change: the agent still writes its question and its behaviour is
// untouched; these surfaces simply stop rendering that text for the
// canonical mandatory questions. QUANTITY IS DELIBERATELY ABSENT: the
// agent's quantity question carries information ("You have indicated 30
// laptops...") - it is a confirmation, not a blank field - so it renders
// as written. Any field_key not in this table falls back to the agent's
// text, which is what makes an unrecognised key safe.
export const STANDARD_QUESTION_LABELS = Object.freeze({
  need_by_date: 'Delivery Date',
  start_date: 'Service Start Date',
  end_date: 'Service End Date',
  rfp_currency: 'RFP Currency',
  budget: 'Estimated Budget',
  ship_to_location: 'Ship-To Location',
  submission_deadline: 'Submission Deadline',
});

export const hasStandardLabel = (fieldKey) =>
  Boolean(fieldKey) && Object.prototype.hasOwnProperty.call(STANDARD_QUESTION_LABELS, fieldKey);

export const displayQuestionText = (question) => {
  const nq = normalizeQuestion(question);
  return hasStandardLabel(nq.field_key) ? STANDARD_QUESTION_LABELS[nq.field_key] : nq.question;
};

// The budget question's parenthetical note moves out of the question body
// (the standard label replaces the body) to a footnote under the control.
// The vendor-question agent redacts the budget answer from everything a
// vendor sees (agents.py, "[redacted - budget information is not shared
// with vendors]"), which is the second half of this sentence.
export const BUDGET_FOOTNOTE =
  'Used to determine approval requirements and for bid comparison. Not shared with vendors.';

// RELATED FIELDS UNDER ONE HEADING. Adjacency itself comes from the order
// above; a group additionally draws the adjacent items inside one framed
// block with a heading. Only consecutive items matching a group's keys, in
// order, form a group - a missing member leaves the survivor standing
// alone, never a heading over one field.
export const FIELD_GROUPS = Object.freeze([
  Object.freeze({ id: 'currency-budget', heading: 'Currency and budget', fieldKeys: Object.freeze(['rfp_currency', 'budget']) }),
]);

export const orderQuestionsForDisplay = (businessQuestions, procurementType, boqPresent = false) => {
  const canonical = getMandatoryFieldKeysForType(procurementType, boqPresent);
  const rank = new Map(canonical.map((key, i) => [key, i]));
  const items = (businessQuestions || []).map((q, index) => {
    const nq = normalizeQuestion(q);
    return {
      index,
      fieldKey: nq.field_key,
      question: nq.question,
      label: displayQuestionText(q),
      isMandatory: rank.has(nq.field_key) || Boolean(nq.is_mandatory),
    };
  });
  const canonicalItems = items
    .filter((it) => rank.has(it.fieldKey))
    .sort((a, b) => (rank.get(a.fieldKey) - rank.get(b.fieldKey)) || (a.index - b.index));
  const flaggedItems = items.filter((it) => !rank.has(it.fieldKey) && it.isMandatory);
  const optionalItems = items.filter((it) => !it.isMandatory);
  return [...canonicalItems, ...flaggedItems, ...optionalItems].map((it, pos) => ({ ...it, number: pos + 1 }));
};

/** {storedIndex: displayNumber} for anything that names a question by
 * number outside the list itself (the missing-mandatory summary). */
export const displayNumberByIndex = (ordered) =>
  Object.fromEntries((ordered || []).map((it) => [it.index, it.number]));

/** Splits an ordered list into render segments: {kind: 'group', group,
 * items} where consecutive items match a FIELD_GROUP's keys in order,
 * {kind: 'single', item} otherwise. Numbers are untouched - a grouped
 * question keeps its own number. */
export const segmentOrderedQuestions = (ordered) => {
  const segments = [];
  const list = ordered || [];
  let i = 0;
  while (i < list.length) {
    const group = FIELD_GROUPS.find((g) =>
      g.fieldKeys.every((key, offset) => list[i + offset] && list[i + offset].fieldKey === key));
    if (group) {
      segments.push({ kind: 'group', group, items: list.slice(i, i + group.fieldKeys.length) });
      i += group.fieldKeys.length;
    } else {
      segments.push({ kind: 'single', item: list[i] });
      i += 1;
    }
  }
  return segments;
};

// CONTROLS SIZED TO CONTENT (item 5). A date, a currency select or a
// budget input does not need the full column; the QUESTION TEXT still
// spans its column - only the control wrapper is capped, never the label.
// LOCATION (item 6): capped to half its cell to leave room beside it for
// geographic_scope (C1b-2, unbuilt). NOTHING is rendered in that room - an
// empty box for an unbuilt field is dead UI. Keys are resolveAnswerControl
// kinds (components/SmartAnswerInput.jsx); anything unlisted is uncapped.
export const CONTROL_WIDTH_CLASS = Object.freeze({
  date: 'max-w-[240px]',
  currency: 'max-w-xs',
  budget: 'max-w-xs',
  location: 'md:max-w-[calc(50%-0.75rem)]',
});

export const controlWidthClass = (controlKind) => CONTROL_WIDTH_CLASS[controlKind] || '';
